import "@/App.css";
import { useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastProvider, useToast } from "@/components/SimpleToast";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OfflineProvider } from "@/context/OfflineContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import SerialNumbers from "@/pages/SerialNumbers";
import ServiceReports from "@/pages/ServiceReports";
import ServiceReportForm from "@/pages/ServiceReportForm";
import Kalender from "@/pages/Kalender";
import Settings from "@/pages/Settings";
import UserManagement from "@/pages/UserManagement";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Mailing from "@/pages/Mailing";
import Praxissuche from "@/pages/Praxissuche";
import TeamChat from "@/pages/TeamChat";
import Planung from "@/pages/Planung";
import MedicalSoftware from "@/pages/MedicalSoftware";
import VendorProfile from "@/pages/VendorProfile";
import Mailbox from "@/pages/Mailbox";
import Diagnose from "@/pages/Diagnose";
import Setup2FA from "@/pages/Setup2FA";
import InviteAccept from "@/pages/InviteAccept";
import ErrorLog from "@/pages/ErrorLog";
import Visitenkarten from "@/pages/Visitenkarten";
import WebinarBackgrounds from "@/pages/WebinarBackgrounds";
import DigitalBusinessCard from "@/pages/DigitalBusinessCard";
import Layout from "@/components/Layout";
import { ErrorBoundary, installGlobalErrorHandlers } from "@/components/ErrorBoundary";
import { useVersionCheck } from "@/hooks/useVersionCheck";

// Install global error handlers ONCE at module load
installGlobalErrorHandlers();

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // 2FA-Pflicht: Zugang zum CRM erst nach vollständig eingerichteter 2FA.
  // Verhindert das Umgehen des Einrichtungs-Schritts per direkter URL.
  if (!(user.totp_enabled && user.totp_verified)) {
    return <Navigate to="/setup-2fa" replace />;
  }
  
  return children;
};

function VersionWatcher() {
  const toast = useToast();
  const onMismatch = useCallback(() => {
    toast.info("Neue Version verfügbar - bitte Seite neu laden (Strg+R)");
  }, [toast]);
  useVersionCheck(onMismatch);
  return null;
}

function App() {
  useEffect(() => {
    // Log app boot for debugging
    // eslint-disable-next-line no-console
    console.info(`[R-Network CRM] Boot v${process.env.REACT_APP_VERSION || "dev"}`);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <OfflineProvider>
              <VersionWatcher />
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/invite" element={<InviteAccept />} />
                  <Route path="/diagnose" element={<Diagnose />} />
                  <Route path="/setup-2fa" element={<Setup2FA />} />
                  <Route path="/vk/:slug" element={<DigitalBusinessCard />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/customers" element={<Customers />} />
                            <Route path="/customers/:id" element={<CustomerDetail />} />
                            <Route path="/serial-numbers" element={<SerialNumbers />} />
                            <Route path="/service-reports" element={<ServiceReports />} />
                            <Route path="/service-reports/new" element={<ServiceReportForm />} />
                            <Route path="/service-reports/:id/edit" element={<ServiceReportForm />} />
                            <Route path="/kalender" element={<Kalender />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/users" element={<UserManagement />} />
                            <Route path="/tasks" element={<Planung initialTab="aufgaben" />} />
                            <Route path="/mein-tag" element={<Planung initialTab="heute" />} />
                            <Route path="/meine-woche" element={<Planung initialTab="woche" />} />
                            <Route path="/planung" element={<Planung initialTab="aufgaben" />} />
                            <Route path="/aufgaben" element={<Navigate to="/tasks" replace />} />
                            <Route path="/knowledge" element={<KnowledgeBase />} />
                            <Route path="/mailing" element={<Mailing />} />
                            <Route path="/praxissuche" element={<Praxissuche />} />
                            <Route path="/visitenkarten" element={<Visitenkarten />} />
                            <Route path="/webinar-hintergruende" element={<WebinarBackgrounds />} />
                            <Route path="/chat" element={<TeamChat />} />
                            <Route path="/medical-software" element={<MedicalSoftware />} />
                            <Route path="/medical-software/:id" element={<VendorProfile />} />
                            <Route path="/mailbox" element={<Mailbox />} />
                            <Route path="/admin/errors" element={<ErrorLog />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </OfflineProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
